import { useLanguage } from "@/contexts/LanguageContext";
import { copyToClipboard, cropId, getFriendlyAccountType } from "@/lib/utils";
import {
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@radix-ui/react-tooltip";
import { Tooltip } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";
import { Transaction } from "@/types";

type TransactionAccountInformationProps = {
  type: string;
  account: Transaction["fromAccount"] | Transaction["toAccount"] | undefined;
};

export default function TransactionAccountInformation({
  account,
  type,
}: TransactionAccountInformationProps) {
  const { t } = useLanguage();

  if (!account)
    return (
      <span className="text-muted-foreground italic text-sm">
        {t("confirmation.notAvailable")}
      </span>
    );

  return (
    <div className="bg-secondary p-3 rounded-md">
      <div className="font-medium text-foreground mb-1.5">{type}</div>
      <div className="space-y-1">
        <div className="flex items-center">
          <span className="text-muted-foreground w-10 text-xs">
            {t("confirmation.type")}
          </span>
          <span className="font-medium ml-1 text-sm">
            {getFriendlyAccountType(account.type)}
          </span>
        </div>
        <div className="flex items-center">
          <span className="text-muted-foreground w-10 text-xs">
            {t("confirmation.id")}
          </span>
          <div className="flex items-center ml-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger
                  className="font-medium text-foreground text-sm hover:underline cursor-help"
                  data-full-id={account.id}
                >
                  {cropId(account.id)}
                </TooltipTrigger>
                <TooltipContent className="max-w-xs break-all">
                  <p>{account.id}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 ml-1 text-gray-500 hover:text-gray-700"
              onClick={() => copyToClipboard(account.id, t("common.id"), t)}
            >
              <Copy className="h-3 w-3" />
              <span className="sr-only">Copy account ID</span>
            </Button>
          </div>
        </div>
        {account.title ? (
          <div
            className="mt-1 text-foreground font-medium truncate text-sm"
            title={account.title}
          >
            {account.title}
          </div>
        ) : (
          <div className="mt-1 text-muted-foreground italic text-xs">
            {t("confirmation.sensitiveData")}
          </div>
        )}
      </div>
    </div>
  );
}
